import { CallDto } from '@act/shared/data-transfer-objects';
import { Box, Typography } from '@mui/material';
import { useContext, useEffect, useRef } from 'react';
import RichTextEditor from 'react-components/dialog-forms/rich-text-editor';
import { TrackingProvider } from 'react-components/tracking';
import { useForm } from 'react-hook-form';
import { callsRequests } from 'redux/requests/calls.request';
import { useApiCall } from 'redux/utils/api-hook';
import PostCallSummaryContext from './PostCallSummaryContext';

interface NotesPaneProps {
  voiceCall: CallDto;
}

interface NotesForm {
  note: string;
}

const NotesPane = ({ voiceCall }: NotesPaneProps) => {
  const { isReadOnly } = useContext(PostCallSummaryContext);
  const { control, setValue } = useForm<NotesForm>({
    defaultValues: {
      note: voiceCall?.note || '',
    },
  });

  // Track the call ID to detect when we switch to a different call
  const currentCallIdRef = useRef<number | undefined>(voiceCall?.id);

  const [_updatedCall, _isUpdatingCall, updateCall] = useApiCall(
    callsRequests.updateCall
  );

  // Only update form value when the call ID changes (switching to a different call)
  // This prevents auto-save from overriding user input on the same call
  useEffect(() => {
    if (voiceCall?.id !== currentCallIdRef.current) {
      // We're switching to a different call, update the form value
      setValue('note', voiceCall?.note || '');
      currentCallIdRef.current = voiceCall?.id;
    }
  }, [voiceCall?.id, voiceCall?.note, setValue]);

  function handleAutoSave(autoSaveNote: string) {
    updateCall({ params: { id: voiceCall.id }, body: { note: autoSaveNote } });
  }

  return (
    <TrackingProvider contextName="notes-pane">
      <Box
        sx={{
          display: 'flex',
          height: '100%',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 2.5,
          boxSizing: 'border-box',
          padding: '0 4px',
        }}
      >
        <Box sx={{ mt: 2 }}>
          <Typography variant="titleMedium">My notes</Typography>
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <RichTextEditor
            key={`note-${voiceCall?.id}-${
              voiceCall?.note ? 'has-note' : 'no-note'
            }`}
            control={control}
            name={'note'}
            autoSaveFn={handleAutoSave}
            height="100%"
            readOnly={isReadOnly}
            trackingName="notes-rich-text-editor"
          />
        </Box>
      </Box>
    </TrackingProvider>
  );
};

export default NotesPane;
